import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty, upperCase } from "lodash";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { getVersionData } from "@redux/version/selector";
import NavigationPage from "@icoach/navigation/NavigationPage";
import PosScreenPage from "@icoach/pos/PosScreenPage";
import Main from "@icoach/main/Main";
import { getIsAndroid, flatDeepArray, localUser, isFunction } from "@util/utils";
import ReactGA from "react-ga4";
import useNavigationApi from "@apis/useNavigationApi";
import useAppSystemApi from "@apis/useAppSystemApi";
import { setMenusData } from "@redux/menus/action";
import { setVersionData } from "@redux/version/action";
import useShowLoading from "@util/hook/useShowLoading";
import { CLUB_LOGIN_ROUTER, NOTIFICATIONS_ROUTER, CLUB_POS_ROUTER, CLUB_INVENTORY_ROUTER } from "@icoach/router/routerPath";
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Box } from "@curves/ui-base";
import NotificationNavBox from "@icoach/notifications/NotificationNavBox";
import InventoryLayout from "@clubInventory/layout/InventoryLayout";
import useMessageDialog from "@util/hook/useMessageDialog";
import useInactivityTimer from "@util/hook/useInactivityTimer";
import useAuthUserApi from "@apis/useAuthUserApi";
import useAuthApi from "@apis/useAuthApi";

const Home = props => {
	const { path: rootPath } = props;
	const showLoading = useShowLoading();
	const { getWebClubInfoApi } = useAppSystemApi();
	const { getMeMenusApi } = useNavigationApi();
	const _dispatch = useDispatch();
	const reduxVersion = useSelector(getVersionData);
	const setMessageDialog = useMessageDialog();
	const { deleteEmployeeAuthPwdApi } = useAuthUserApi();
	const { getLogoutApi } = useAuthApi();
	// 設定 Inactivity Timer 相關的參數
	const inactivityTimerConfig = useMemo(
		() => {
			let firstTimeout = 30 * 60 * 1000; // 第一階段超時時間：30 分鐘（毫秒）
			let secondTimeout = 3 * 60 * 1000; // 第二階段超時時間：3 分鐘（毫秒）
			let inactivityCheckInterval = 3 * 60 * 1000; // 閒置檢查間隔：3 分鐘（毫秒）

			/**
			 * 用戶登出 API 呼叫函數
			 * 1. 若當前用戶存在（`employee` 有值），則調用 `deleteEmployeeAuthPwdApi` 來刪除用戶驗證資訊
			 * 2. 否則，執行 `getLogoutApi` 來登出用戶
			 */
			const logOutApi = async () => {
				const employee = localUser.get("employee");
				if (!isEmpty(employee)) {
					await deleteEmployeeAuthPwdApi(employee.token); // 調用 API 刪除員工驗證
				} else {
					await getLogoutApi(); // 執行一般登出 API
				}
			};

			return {
				firstTimeout,
				secondTimeout,
				inactivityCheckInterval,

				/**
				 * 第一階段超時回調函數
				 * 1. 顯示警告彈窗，提醒用戶閒置過久
				 * 2. 用戶可透過 "確認" & "X" 按鈕重置活動時間
				 *
				 * @param {Function} updateLastActivityTime - 用於重置最近活動時間的函數
				 */
				onFirstTimeout: updateLastActivityTime => {
					setMessageDialog({
						open: true,
						title: "重要提醒",
						msg: "系統偵測到您已超過30分鐘未進行操作，是否需要繼續使用？若無操作，將於3分鐘後登出教練權限。",
						buttonText: "確認",
						onClose: () => {
							if (isFunction(updateLastActivityTime)) updateLastActivityTime();
						},
						onConfirm: () => {
							if (isFunction(updateLastActivityTime)) updateLastActivityTime();
						},
					});
				},

				/**
				 * 第二階段超時回調函數
				 * 1. 關閉提醒彈窗
				 * 2. 執行登出流程
				 */
				onSecondTimeout: () => {
					setMessageDialog({
						open: false,
					});
					logOutApi();
				},
			};
		},
		// eslint-disable-next-line
		[setMessageDialog, deleteEmployeeAuthPwdApi, getLogoutApi]
	);

	// 啟動 inactivity timer
	useInactivityTimer(inactivityTimerConfig);

	const getInitData = async () => {
		showLoading(true);
		const menuData = await getMeMenusApi();
		const versionData = await getWebClubInfoApi();
		if (menuData) {
			const { menus = [], clubs = [] } = menuData;
			if (clubs[0]) localUser.setClubData(clubs[0]);
			_dispatch(setMenusData({ menulist: flatDeepArray(menus), oriMenuData: menus }));
		}
		if (versionData) {
			_dispatch(setVersionData(versionData));
		}
		showLoading(false);
	};

	useEffect(
		() => {
			if (!isEmpty(reduxVersion) && reduxVersion?.env) {
				const { env } = reduxVersion;
				if (upperCase(env) === "PRODUCTION") {
					ReactGA.initialize("G-RBG571BKB5");
				} else if (upperCase(env) === "UAT") {
					ReactGA.initialize("G-JSYHPF7NF2");
				}
			}
		},
		// eslint-disable-next-line
		[reduxVersion]
	);

	useEffect(
		() => {
			getInitData();
			if (getIsAndroid()) {
				document.body.classList.add("mobile");
			}
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<Router>
			<Box className="icoach">
				<Switch>
					<PrivateRoute path={CLUB_INVENTORY_ROUTER.BASE} as={InventoryLayout} />
					<PrivateRoute path={CLUB_LOGIN_ROUTER.ROOT} as={NavigationPage} />
					<PrivateRoute path={NOTIFICATIONS_ROUTER.BASE} as={NotificationNavBox} />
					<PrivateRoute path={CLUB_POS_ROUTER.BASE} as={PosScreenPage} />
				</Switch>
				<Main path={rootPath} className="main-container" />
			</Box>
		</Router>
	);
};

export default Home;
